import type { QueryObserverResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useSharedListsAccess } from "~/shared/hooks/use-shared-lists-access.ts";
import type {
	CommodityNewBody,
	CommodityUpdateBody,
} from "~/shared/schemas/commodity-schema.ts";
import { commoditySchema } from "~/shared/schemas/commodity-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	deleteAndValidate,
	getAndValidate,
	postAndValidate,
	putAndValidate,
} from "~/shared/services/api/fetch-client.ts";
import type { SavedCommodities } from "~/shared/types/hooks-types";

export const useSavedCommodities = (
	customerId: string,
	enabled = true,
	isInternal = false,
): QueryObserverResult<SavedCommodities> => {
	const { canManageSharedLists } = useSharedListsAccess();

	return useQuery<SavedCommodities>({
		queryKey: ["savedCommodities", customerId],
		queryFn: async (): Promise<SavedCommodities> => {
			const url = isInternal
				? apiRoutes.SAVEDCOMMODITIES.replace(":customerId", customerId)
				: apiRoutes.CUSTOMERSAVEDCOMMODITIES;
			const savedCommoditiesResult = await getAndValidate(
				url,
				commoditySchema.array(),
			);

			if (!savedCommoditiesResult.success) {
				throw new Error(savedCommoditiesResult.error);
			}

			return { savedCommodities: savedCommoditiesResult.data };
		},
		enabled:
			enabled &&
			canManageSharedLists &&
			Boolean(customerId) &&
			customerId !== "0" &&
			customerId.trim() !== "",
		staleTime: 1000 * 60 * 30, // 30 minutes
		gcTime: 1000 * 60 * 60, // 1 hour
		refetchOnMount: true,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		retry: 5,
	});
};

export const useCreateSavedCommodity = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: CommodityNewBody) => {
			const url = body.isInternal
				? apiRoutes.SAVEDCOMMODITIES
				: apiRoutes.CUSTOMERSAVEDCOMMODITIES;
			const result = await postAndValidate(url, body, z.unknown());

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["savedCommodities"] });
		},
	});
};

export const useUpdateSavedCommodity = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (body: CommodityUpdateBody) => {
			const result = await putAndValidate(
				apiRoutes.UPDATESAVEDCOMMODITIES.replace(":id", String(body.id)),
				body,
				z.unknown(),
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["savedCommodities"] });
		},
	});
};

export const useDeleteSavedCommodity = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			const result = await deleteAndValidate(
				apiRoutes.DELETESAVEDCOMMODITY.replace(":id", String(id)),
				z.unknown(),
			);

			if (!result.success) {
				throw new Error(result.error);
			}

			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["savedCommodities"] });
		},
	});
};
